/**
 * React Query hooks for Admin Invitation management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import {
  fetchInvitations,
  inviteAdmin,
  resendInvitation,
  revokeInvitation,
  type FetchInvitationsParams,
} from '../services/adminInvitationService';
import type { InviteAdminPayload } from '../types/adminInvitation';

const INVITATIONS_QUERY_KEY = ['admin', 'invitations'];

export function useInvitationsQuery(params: FetchInvitationsParams = {}) {
  return useQuery({
    queryKey: [...INVITATIONS_QUERY_KEY, params],
    queryFn: () => fetchInvitations(params),
  });
}

export function useInviteAdminMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: InviteAdminPayload) => inviteAdmin(payload),
    onSuccess: () => {
      message.success('Đã gửi lời mời thành công');
      queryClient.invalidateQueries({ queryKey: INVITATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['admin', 'admins'] });
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      message.error(err.response?.data?.message || 'Gửi lời mời thất bại');
    },
  });
}

export function useResendInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) => resendInvitation(invitationId),
    onSuccess: () => {
      message.success('Đã gửi lại lời mời thành công');
      queryClient.invalidateQueries({ queryKey: INVITATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['admin', 'admins'] });
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      message.error(err.response?.data?.message || 'Gửi lại lời mời thất bại');
    },
  });
}

export function useRevokeInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) => revokeInvitation(invitationId),
    onSuccess: () => {
      message.success('Đã hủy lời mời');
      queryClient.invalidateQueries({ queryKey: INVITATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['admin', 'admins'] });
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      message.error(err.response?.data?.message || 'Hủy lời mời thất bại');
    },
  });
}
